import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ITMS } from "@/constants/testIds";
import { useDepots, fmtDepot } from "@/lib/depots";
import {
  Bus, Lightning, Wrench, Warning, BatteryCharging, Handshake,
  CheckCircle, ArrowsClockwise, CurrencyInr, Users, Ticket, MapTrifold,
  CaretUp, CaretDown, Minus, Database, ArrowUpRight,
} from "@phosphor-icons/react";
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar, ResponsiveContainer,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { MapContainer, TileLayer, Marker, Tooltip as LTooltip } from "react-leaflet";
import { busIcon, TILE_URL, TILE_ATTR, STATUS_COLOR } from "@/lib/mapIcons";
import LeakageDetector from "@/components/LeakageDetector";
import BackupBusWidget from "@/components/BackupBusWidget";

const KPI = [
  { key: "total_fleet", label: "Total Fleet", icon: Bus, color: "#1E3A8A", tid: ITMS.kpiTotalFleet },
  { key: "active", label: "Active Buses", icon: Lightning, color: "#22C55E", tid: ITMS.kpiActive },
  { key: "on_trip", label: "On Trip", icon: ArrowsClockwise, color: "#1E3A8A", tid: ITMS.kpiOnTrip },
  { key: "available", label: "Available", icon: CheckCircle, color: "#22C55E", tid: ITMS.kpiAvailable },
  { key: "maintenance", label: "Maintenance", icon: Wrench, color: "#F97316", tid: ITMS.kpiMaintenance },
  { key: "breakdown", label: "Breakdown", icon: Warning, color: "#EF4444", tid: ITMS.kpiBreakdown },
  { key: "charging", label: "Charging", icon: BatteryCharging, color: "#06B6D4", tid: ITMS.kpiCharging },
  { key: "private", label: "Private Operator", icon: Handshake, color: "#8B5CF6", tid: ITMS.kpiPrivate },
];

const fmt = (n) => n?.toLocaleString("en-IN");
const inr = (n) => "₹" + (n / 100000).toFixed(2) + "L";

export default function Dashboard() {
  const navigate = useNavigate();
  const depotMap = useDepots();
  const [kpis, setKpis] = useState({});
  const [trends, setTrends] = useState({});
  const [buses, setBuses] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [depots, setDepots] = useState([]);
  const [health, setHealth] = useState({ modules: [], ready_count: 0, empty_count: 0, total_modules: 0 });
  const [trendGran, setTrendGran] = useState("daily"); // daily | weekly | monthly

  const fetchAll = async (granularity = trendGran) => {
    const [k, t, b, a, d, h] = await Promise.all([
      api.get("/dashboard/kpis"),
      api.get(`/dashboard/trends?granularity=${granularity}`),
      api.get("/fleet/buses"), api.get("/alerts"), api.get("/dashboard/depot-summary"),
      api.get("/dashboard/data-health"),
    ]);
    setKpis(k.data); setTrends(t.data); setBuses(b.data); setAlerts(a.data); setDepots(d.data);
    setHealth(h.data);
  };

  // Just refetch trends when granularity changes (keeps the rest of dashboard stable)
  const reloadTrends = async (g) => {
    setTrendGran(g);
    const { data } = await api.get(`/dashboard/trends?granularity=${g}`);
    setTrends(data);
  };

  useEffect(() => {
    fetchAll();
    const t = setInterval(() => fetchAll(trendGran), 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusColor = (s) => STATUS_COLOR[s] || "#64748B";

  return (
    <div className="p-4 lg:p-6 space-y-5 lg:space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-cap text-slate-500">Operations Overview</div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold mt-1 leading-tight">Goa Fleet · Live Dashboard</h1>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="status-dot status-Available"></span>
          <span className="text-slate-600">Auto-refresh · 15s</span>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        {KPI.map((k) => (
          <div key={k.key} data-testid={k.tid}
               className="ktcl-card ktcl-card-hover p-4">
            <div className="flex items-center justify-between">
              <div className="label-cap text-slate-500">{k.label}</div>
              <k.icon size={18} weight="duotone" style={{ color: k.color }} />
            </div>
            <div className="mt-3 font-display text-2xl font-bold tabular-nums" style={{ color: k.color }}>
              {fmt(kpis[k.key])}
            </div>
            <div className="text-[10px] text-slate-500 mt-1">
              {kpis[k.key] > 0 ? "Live count" : "Awaiting real data"}
            </div>
          </div>
        ))}
      </div>

      {/* Revenue + Map */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 ktcl-card p-4">
          {(() => null)()}
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div>
              <div className="label-cap text-slate-500">Revenue & Passengers</div>
              <h3 className="font-display text-lg font-bold">
                {trendGran === "daily" ? "Weekly Trend" : trendGran === "weekly" ? "12-Week Trend" : "12-Month Trend"}
              </h3>
            </div>
            <div className="flex gap-3 text-xs items-center">
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#1E3A8A]"></span>Revenue ({trendGran === "monthly" ? "₹Cr" : "₹L"})</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#22C55E]"></span>Passengers ({trendGran === "monthly" ? "L" : "K"})</span>
              <div className="flex bg-slate-100 rounded-md p-0.5 ml-1" role="tablist" aria-label="Trend granularity">
                {[
                  { k: "daily",   l: "Daily" },
                  { k: "weekly",  l: "Weekly" },
                  { k: "monthly", l: "Monthly" },
                ].map((g) => (
                  <button key={g.k} onClick={() => reloadTrends(g.k)}
                    data-testid={`dash-trend-${g.k}`}
                    role="tab" aria-selected={trendGran === g.k}
                    className={`px-2.5 py-1 text-[11px] font-bold rounded transition-colors
                      ${trendGran === g.k ? "bg-white text-[#1E3A8A] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                    {g.l}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={(trends.revenue || []).map((r, i) => {
              const revDiv = trendGran === "monthly" ? 10000000 : 100000;       // ₹Cr | ₹L
              const paxDiv = trendGran === "monthly" ? 100000 : 1000;             // L  | K
              return {
                day: r.day,
                revenue: +(r.value / revDiv).toFixed(2),
                passengers: +((trends.passengers?.[i]?.value || 0) / paxDiv).toFixed(2),
              };
            })}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1E3A8A" stopOpacity={0.4}/>
                  <stop offset="100%" stopColor="#1E3A8A" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22C55E" stopOpacity={0.3}/>
                  <stop offset="100%" stopColor="#22C55E" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
              <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="#94A3B8" interval={trendGran === "monthly" ? 0 : trendGran === "weekly" ? 1 : 0}/>
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} stroke="#1E3A8A"
                tickFormatter={(v) => trendGran === "monthly" ? `₹${v}Cr` : `₹${v}L`}/>
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} stroke="#22C55E"
                tickFormatter={(v) => trendGran === "monthly" ? `${v}L` : `${v}K`}/>
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }}
                formatter={(value, name) => {
                  if (name === "revenue") return [`${trendGran === "monthly" ? "₹" + value + " Cr" : "₹" + value + " L"}`, "Revenue"];
                  return [`${value} ${trendGran === "monthly" ? "Lakh pax" : "K pax"}`, "Passengers"];
                }}/>
              <Area yAxisId="left" type="monotone" dataKey="revenue" stroke="#1E3A8A" fill="url(#g1)" strokeWidth={2}/>
              <Area yAxisId="right" type="monotone" dataKey="passengers" stroke="#22C55E" fill="url(#g2)" strokeWidth={2}/>
            </AreaChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-2 pt-3 border-t border-slate-100">
            <div data-testid="dash-rev-today">
              <div className="label-cap text-slate-500">Today</div>
              <div className="font-display font-bold text-base lg:text-lg text-[#1E3A8A]">{inr(kpis.revenue_today || 0)}</div>
              <Delta cur={kpis.revenue_today} prev={kpis.revenue_yesterday} suffix="vs yesterday" tid="delta-today"/>
            </div>
            <div data-testid="dash-rev-yesterday">
              <div className="label-cap text-slate-500">Yesterday</div>
              <div className="font-display font-bold text-base lg:text-lg">{inr(kpis.revenue_yesterday || 0)}</div>
            </div>
            <div data-testid="dash-rev-week">
              <div className="label-cap text-slate-500">This Week</div>
              <div className="font-display font-bold text-base lg:text-lg">{inr(kpis.revenue_week || 0)}</div>
              <Delta cur={kpis.revenue_week} prev={kpis.revenue_last_week} suffix="vs last wk" tid="delta-week"/>
            </div>
            <div data-testid="dash-rev-month">
              <div className="label-cap text-slate-500">Month</div>
              <div className="font-display font-bold text-base lg:text-lg">{inr(kpis.revenue_month || 0)}</div>
              <Delta cur={kpis.revenue_month} prev={kpis.revenue_last_month} suffix="vs last mo" tid="delta-month"/>
            </div>
            <div data-testid="dash-rev-year">
              <div className="label-cap text-slate-500">Year</div>
              <div className="font-display font-bold text-base lg:text-lg">{inr(kpis.revenue_year || 0)}</div>
              <Delta cur={kpis.revenue_year} prev={kpis.revenue_last_year} suffix="vs last yr" tid="delta-year"/>
            </div>
          </div>
        </div>

        <div className="ktcl-card p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-cap text-slate-500">Live Fleet</div>
              <h3 className="font-display text-lg font-bold flex items-center gap-2">
                <MapTrifold size={18} weight="duotone" className="text-[#1E3A8A]" />
                Goa Network
              </h3>
            </div>
            <span className="text-xs text-slate-500">{buses.length} units</span>
          </div>
          <div className="rounded-md overflow-hidden border border-slate-200 h-[240px]">
            <MapContainer center={[15.39, 73.99]} zoom={9} style={{ height: "100%", width: "100%" }}>
              <TileLayer attribution={TILE_ATTR} url={TILE_URL}/>
              {buses.slice(0, 40).map((b) => (
                <Marker key={b.id} position={[b.lat, b.lng]} icon={busIcon(b.status, 26)}>
                  <LTooltip>{b.fleet_id} · {b.status}</LTooltip>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </div>
      </div>

      {/* Bottom: Depot table + Alerts + Ticketing */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="ktcl-card p-4">
          <div className="label-cap text-slate-500">Depot Summary</div>
          <h3 className="font-display text-lg font-bold mb-3">Fleet by Depot</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                <th className="py-2 font-medium">Depot</th>
                <th className="font-medium text-right">Total</th>
                <th className="font-medium text-right">Active</th>
                <th className="font-medium text-right">Maint.</th>
                <th className="font-medium text-right">B/D</th>
              </tr>
            </thead>
            <tbody>
              {depots.map((d) => (
                <tr key={d.depot} className="border-b border-slate-100 last:border-0">
                  <td className="py-2 font-medium">{fmtDepot(d.depot, depotMap)}</td>
                  <td className="text-right tabular-nums">{d.total}</td>
                  <td className="text-right tabular-nums text-[#22C55E]">{d.active}</td>
                  <td className="text-right tabular-nums text-[#F97316]">{d.maintenance}</td>
                  <td className="text-right tabular-nums text-[#EF4444]">{d.breakdown}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="ktcl-card p-4">
          <div className="label-cap text-slate-500">Live Feed</div>
          <h3 className="font-display text-lg font-bold mb-3">Recent Alerts</h3>
          <div className="space-y-2 max-h-[300px] overflow-auto scroll-thin pr-1">
            {alerts.slice(0, 12).map((a) => (
              <div key={a.id} className="flex items-start gap-3 p-2 rounded-md hover:bg-slate-50 transition-colors">
                <div className={`w-1 self-stretch rounded-full ${a.severity === "High" ? "bg-[#EF4444]" : a.severity === "Medium" ? "bg-[#F97316]" : "bg-slate-300"}`}></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{a.type} · <span className="text-slate-500 font-normal">{a.fleet_id}</span></div>
                  <div className="text-xs text-slate-500 truncate">{a.message}</div>
                </div>
                <div className="text-[10px] text-slate-400 whitespace-nowrap">
                  {new Date(a.timestamp).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="ktcl-card p-4" data-testid="dashboard-data-health">
          <div className="flex items-start justify-between gap-2 mb-3">
            <div>
              <div className="label-cap text-slate-500 flex items-center gap-1.5">
                <Database size={11} weight="bold"/> Real-Data Health
              </div>
              <h3 className="font-display text-lg font-bold">
                {health.ready_count}<span className="text-slate-400 font-normal text-base"> / {health.total_modules}</span>
                <span className="text-[12px] font-normal text-slate-500 ml-2">modules loaded</span>
              </h3>
            </div>
            <button onClick={() => navigate("/data-manager")}
              data-testid="dashboard-data-health-upload"
              className="text-[10.5px] font-bold px-2 py-1 rounded-md bg-[#1E3A8A] text-white hover:bg-[#1E40AF] inline-flex items-center gap-1">
              Upload <ArrowUpRight size={11} weight="bold"/>
            </button>
          </div>

          {/* Progress bar */}
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-3" aria-hidden="true">
            <div className="h-full bg-[#22C55E] transition-all"
              style={{ width: `${health.total_modules ? (health.ready_count*100/health.total_modules) : 0}%` }}/>
          </div>

          {/* Module rows */}
          <div className="space-y-1.5 max-h-[230px] overflow-auto scroll-thin pr-1">
            {(health.modules || []).map((m) => {
              const ready = m.count > 0;
              return (
                <button key={m.collection}
                  onClick={() => m.link ? navigate(m.link) : null}
                  disabled={!m.link}
                  data-testid={`health-row-${m.collection}`}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md text-[12.5px] transition-colors ${
                    m.link ? "hover:bg-blue-50" : ""
                  } ${ready ? "" : "bg-amber-50/40"}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    {ready
                      ? <CheckCircle size={13} weight="fill" className="text-[#22C55E] shrink-0"/>
                      : <Warning size={13} weight="fill" className="text-amber-500 shrink-0"/>}
                    <span className={`truncate font-medium ${ready ? "text-slate-700" : "text-slate-600"}`}>
                      {m.label}
                    </span>
                  </div>
                  <span className={`tabular-nums font-bold shrink-0 ml-2 ${ready ? "text-slate-900" : "text-amber-700"}`}>
                    {m.count.toLocaleString("en-IN")}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Backup bus suggestions for breakdowns */}
      <BackupBusWidget />

      {/* Revenue Leakage Detector */}
      <LeakageDetector compact />

      {/* Occupancy by hour */}
      <div className="ktcl-card p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="label-cap text-slate-500">Hourly Analytics</div>
            <h3 className="font-display text-lg font-bold">Occupancy through the day</h3>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={trends.occupancy || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
            <XAxis dataKey="hour" tick={{ fontSize: 11 }} stroke="#94A3B8"/>
            <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8" unit="%" />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }}/>
            <Line type="monotone" dataKey="value" stroke="#1E3A8A" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/** Tiny up/down delta pill (+X.Y% vs previous period). */
function Delta({ cur, prev, suffix, tid }) {
  if (!cur || !prev) return null;
  const pct = ((cur - prev) / prev) * 100;
  if (!isFinite(pct)) return null;
  const flat = Math.abs(pct) < 0.05;
  const up = pct >= 0;
  const Icon = flat ? Minus : up ? CaretUp : CaretDown;
  const color = flat ? "text-slate-500" : up ? "text-emerald-600" : "text-red-600";
  const bg = flat ? "bg-slate-50" : up ? "bg-emerald-50" : "bg-red-50";
  return (
    <div data-testid={tid} aria-label={`${pct >= 0 ? "Up" : "Down"} ${Math.abs(pct).toFixed(1)} percent ${suffix}`}
      className={`inline-flex items-center gap-1 text-[10.5px] font-bold mt-1 px-1.5 py-0.5 rounded ${color} ${bg}`}>
      <Icon size={10} weight="bold"/>
      {Math.abs(pct).toFixed(1)}% <span className="font-normal text-slate-500">{suffix}</span>
    </div>
  );
}

